export type MapSourceGeometryKind = 'point' | 'line' | 'polygon' | 'mixed' | 'unknown';
export type MapSourceAuthoringGeometryKind = Exclude<MapSourceGeometryKind, 'mixed' | 'unknown'>;

export type MapSourceVectorLayerRecord = {
  id: string;
  description?: string;
  minzoom?: number;
  maxzoom?: number;
  detected_geometry: MapSourceGeometryKind;
  geometry_override?: MapSourceGeometryKind;
  effective_geometry: MapSourceGeometryKind;
  fields?: Record<string, unknown>;
  raw: Record<string, unknown>;
};

const GEOMETRY_KIND_ORDER: MapSourceAuthoringGeometryKind[] = ['point', 'line', 'polygon'];

const pickZoomValue = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
};

export const normalizeMapSourceGeometryKind = (value: unknown): MapSourceGeometryKind | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  switch (normalized) {
    case 'point':
    case 'line':
    case 'polygon':
    case 'mixed':
    case 'unknown':
      return normalized;
    default:
      return null;
  }
};

export const normalizeMapSourceGeometryOverrideKind = (value: unknown): MapSourceAuthoringGeometryKind | null => {
  const normalized = normalizeMapSourceGeometryKind(value);
  if (!normalized || normalized === 'mixed' || normalized === 'unknown') {
    return null;
  }
  return normalized;
};

export const isMapSourceGeometryAuthoringReady = (
  geometry: MapSourceGeometryKind | string | null | undefined,
): geometry is MapSourceAuthoringGeometryKind => {
  const normalized = typeof geometry === 'string' ? geometry.trim().toLowerCase() : '';
  return normalized === 'point' || normalized === 'line' || normalized === 'polygon';
};

const collectRawGeometryKinds = (value: unknown, flags: Record<Exclude<MapSourceGeometryKind, 'mixed' | 'unknown'>, boolean>) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return;
    if (normalized === 'mixed') {
      flags.point = true;
      flags.line = true;
      flags.polygon = true;
      return;
    }
    if (normalized.includes('point')) flags.point = true;
    if (normalized.includes('line')) flags.line = true;
    if (normalized.includes('polygon')) flags.polygon = true;
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(entry => collectRawGeometryKinds(entry, flags));
  }
};

const detectGeometryKind = (record: Record<string, unknown>): MapSourceGeometryKind => {
  const direct = normalizeMapSourceGeometryKind(record.detected_geometry);
  if (direct) {
    return direct;
  }

  const flags: Record<Exclude<MapSourceGeometryKind, 'mixed' | 'unknown'>, boolean> = {
    point: false,
    line: false,
    polygon: false,
  };
  collectRawGeometryKinds(record.geometry_type, flags);
  collectRawGeometryKinds(record.geometry, flags);
  collectRawGeometryKinds(record.geometryType, flags);
  collectRawGeometryKinds(record.geometry_types, flags);
  collectRawGeometryKinds(record.geometryTypes, flags);

  const matched = GEOMETRY_KIND_ORDER.filter(kind => flags[kind]);
  if (matched.length === 0) return 'unknown';
  if (matched.length > 1) return 'mixed';
  return matched[0];
};

export const getMapSourceGeometryKinds = (
  geometry: MapSourceGeometryKind | string | null | undefined,
): MapSourceAuthoringGeometryKind[] => {
  const normalized = typeof geometry === 'string' ? geometry.trim().toLowerCase() : '';
  if (!isMapSourceGeometryAuthoringReady(normalized)) return [];
  return GEOMETRY_KIND_ORDER.filter(kind => normalized === kind);
};

export const readMapSourceVectorLayers = (
  layers?: Array<Record<string, unknown> | string> | null,
): MapSourceVectorLayerRecord[] => {
  if (!layers || !Array.isArray(layers)) return [];
  return layers
    .map(layer => {
      const record =
        typeof layer === 'string'
          ? ({ id: layer } satisfies Record<string, unknown>)
          : ((layer ?? {}) as Record<string, unknown>);
      const id =
        (typeof record.id === 'string' && record.id) ||
        (typeof record.layer === 'string' && record.layer) ||
        (typeof record.name === 'string' && record.name) ||
        'layer';
      const detectedGeometry = detectGeometryKind(record);
      const overrideGeometry = normalizeMapSourceGeometryOverrideKind(record.geometry_override);
      return {
        id,
        description: typeof record.description === 'string' ? record.description : undefined,
        minzoom: pickZoomValue(record, ['minzoom', 'minZoom', 'min_zoom']),
        maxzoom: pickZoomValue(record, ['maxzoom', 'maxZoom', 'max_zoom']),
        detected_geometry: detectedGeometry,
        geometry_override: overrideGeometry ?? undefined,
        effective_geometry: overrideGeometry ?? normalizeMapSourceGeometryKind(record.effective_geometry) ?? detectedGeometry,
        fields: record.fields && typeof record.fields === 'object' ? (record.fields as Record<string, unknown>) : undefined,
        raw: record,
      } satisfies MapSourceVectorLayerRecord;
    })
    .filter(layer => Boolean(layer.id));
};

export const applyMapSourceGeometryOverride = (
  layers: Array<Record<string, unknown> | string> | null | undefined,
  layerId: string,
  nextOverride: MapSourceAuthoringGeometryKind | null,
) =>
  (layers ?? []).map(layer => {
    const record =
      typeof layer === 'string'
        ? ({ id: layer } satisfies Record<string, unknown>)
        : ({ ...(layer ?? {}) } as Record<string, unknown>);
    const recordId =
      (typeof record.id === 'string' && record.id) ||
      (typeof record.layer === 'string' && record.layer) ||
      (typeof record.name === 'string' && record.name) ||
      'layer';
    if (recordId !== layerId) {
      return record;
    }
    if (nextOverride) {
      record.geometry_override = nextOverride;
    } else {
      delete record.geometry_override;
    }
    delete record.effective_geometry;
    delete record.detected_geometry;
    return record;
  });
